import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Paper,
  Popover,
  Typography
} from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import SettingsIcon from "@material-ui/icons/Settings";
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import COLORS from "scss/colors.module.scss";
import ApiClient from "utils/ApiClient";
import { formatDate, formatTime } from "../../helpers/dateFormatter";
import moment from "moment";
import { normalizeColOptions } from "app/websocketManager/utils";
import tinycolor from "tinycolor2";
import { Tooltip } from "@material-ui/core";

const expandedViewStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.3)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999
  },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.3)",
    backdropFilter: "blur(2px)",
    zIndex: -1,
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    maxWidth: "80vw",
    maxHeight: "85vh",
    width: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
    animation: "slideIn 0.3s ease-out",
    color: "#111827",
    fontFamily: "'Inter', sans-serif",
    zIndex: 1,
  },
  title: {
    margin: 0,
    position: "relative",
    fontSize: "20px",
    fontWeight: 600,
    color: "#1f2937",
    maxWidth: "550px",
    overflowX: "auto"
  },
  key: {
    width: "25%",
    fontWeight: 600,
    color: "#374151",
    fontSize: "1.1rem",
    wordWrap: "break-word",
    paddingRight: "10px"
  },
  value: {
    width: "75%",
    fontSize: "0.975rem",
    color: "#1f2937",
    wordWrap: "break-word",
    paddingLeft: "10px",
    maxHeight: 210,
    overflowY: "auto",
    whiteSpace: 'pre-wrap',
  },
  subsheetValue: {
    width: "75%",
    fontSize: "0.975rem",
    color: "#1f2937",
    wordWrap: "break-word",
    paddingLeft: "10px",
    maxHeight: 420,
    whiteSpace: 'pre-wrap',
    // overflowY: "auto"
  },
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: "rgba(0,0,0,0.3)",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "stretch",
    zIndex: 9999
  },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.3)",
    backdropFilter: "blur(2px)",
    zIndex: -1,
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "12px 0 0 12px",
    width: "500px",
    maxWidth: "550px",
    height: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "-2px 0 12px rgba(0, 0, 0, 0.15)",
    animation: "slideInRight 0.3s ease-out forwards",
    color: "#111827",
    fontFamily: "'Inter', sans-serif",
    zIndex: 1,
  },
  header: {
    position: "relative",
    padding: "16px 24px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#f9fafb",
  },
  title: {
    margin: 0,
    position: "relative",
    fontSize: "20px",
    fontWeight: 600,
    color: "#1f2937",
    maxWidth: "320px",
    overflowX: "auto"
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "20px",
    color: "#6b7280",
    cursor: "pointer",
    transition: "color 0.2s",
  },
  body: {
    padding: "20px 24px",
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    backgroundColor: "#ffffff",
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    padding: "12px 16px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #ebebeb",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
  },
  key: {
    width: "30%",
    fontWeight: 600,
    color: "#374151",
    fontSize: "1rem",
    wordWrap: "break-word",
    paddingRight: "10px"
  },
  value: {
    width: "70%",
    fontSize: "0.875rem",
    color: "#1f2937",
    wordWrap: "break-word",
    paddingLeft: "10px",
    maxHeight: 130,
    overflowY: "auto",
    whiteSpace: 'pre-wrap',
  },
  subsheetValue: {
    width: "70%",
    fontSize: "0.875rem",
    color: "#1f2937",
    wordWrap: "break-word",
    paddingLeft: "10px",
    maxHeight: 340,
    whiteSpace: 'pre-wrap',
    // overflowY: "auto"
  },
};

const keyframes = `
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0%);
    }
  }
`;

const subsheetStyles = {
  container: { 
    backgroundColor: "#ffffff", 
    maxHeight: 320, 
    overflowY: "auto", 
    overflowX: "auto", 
    borderRadius: "6px", 
    maxWidth: 750, 
    border: "1px solid #e5e7eb",
    transition: "transform 0.2s ease, box-shadow 0.2s ease"
  },
  headerCell: { 
    color: "#1f2937", 
    fontWeight: 600, 
    whiteSpace: "nowrap", 
    backgroundColor: "#f3f4f6",
    minWidth: 100,
    borderBottom: "1px solid #e5e7eb"
  },
  tableCell: {
    whiteSpace: "nowrap",
    minWidth: 100,
    maxHeight: 130,
    overflowY: "auto", 
    color: "#374151",
    borderBottom: "1px solid #f3f4f6"
  },
  rowCell: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.875rem",
    color: "#374151",
    minWidth: 100,
    maxHeight: 120,
    overflowY: "auto"
  }
};

const expandedSubsheetStyles = {
  container: { 
    backgroundColor: "#ffffff", 
    maxHeight: 400,
    overflowY: "auto", 
    overflowX: "auto", 
    borderRadius: "6px", 
    maxWidth: 850, 
    border: "1px solid #e5e7eb",
    transition: "transform 0.2s ease, box-shadow 0.2s ease"
  },
  rowCell: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.975rem",
    color: "#374151",
    minWidth: 130,
    maxHeight: 180,
    overflowY: "auto"
  },
  tableCell: {
    whiteSpace: "nowrap",
    minWidth: 130,
    maxHeight: 130,
    overflowY: "auto", 
    color: "#374151",
    borderBottom: "1px solid #f3f4f6"
  }
};

const RowDetailPanel = ({ params, data, onClose }) => {
  const [formattedData, setFormattedData] = useState({});
  const [metaData, setMetaData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isHeaderSaving, setIsHeaderSaving] = useState(false);
  const [options, setOptions] = useState([]);
  const isAdmin = params.store?.containerStore?.permissions?.isAdmin() ?? false;
  const columns = params.store?.listConfig?.listData;

  const settingsRef = useRef(null);
  const settingsButtonRef = useRef(null);

  const getFormattedHeaderName = () => {
    let headerName = params.store.view.options?.rowViewHeader ?? '';
    if (headerName !== '') {
      const isColumnId = data.hasOwnProperty('column_' + headerName);
      if (isColumnId) {
        if (data?.['column_' + headerName]) {
          return data?.['column_' + headerName];
        }
      } else {
        return headerName;
      }
    }
    return '';
  };

  const [headerTitle, setHeaderTitle] = useState(getFormattedHeaderName() || `Row ${data?.row_id}`);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target) &&
        settingsButtonRef.current &&
        !settingsButtonRef.current.contains(event.target)
      ) {
        setSettingsOpen(false);
      }
    };

    if (settingsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [settingsOpen]);

  useEffect(() => {
    const formatData = async () => {
      setLoading(true);
      const [mainData, meta] = await getRowDataToView(data, columns);
      setFormattedData(mainData);
      setMetaData(meta);
      setLoading(false);
    };

    const createInitialOptions = () => {
      const columns = params.store.columns;
      const columnIdsByName = columns.reduce((acc, column) => {
        if (
          column.id > 0 &&
          column.name.trim() !== '' &&
          column.name.trim().toLowerCase() !== 'star' &&
          column.alias_name !== 'createdBy' &&
          column.column_type_id != '6' &&
          column.column_type_id != '9'
        ) {
          acc[column.name] = column.id;
        }
        return acc;
      }, {});
      let columnNames = Object.keys(columnIdsByName);
      params.store.columnIdsByName = columnIdsByName;
      return columnNames.filter(value => (value !== '' && value !== headerTitle));
    };

    const initOptions = createInitialOptions();
    setOptions(initOptions);
    formatData();
  }, [data, columns]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleSettings = () => {
    if (!isHeaderSaving) {
      setSettingsOpen((prev) => !prev);
    }
  };

  const saveAndReplaceHeaderTitle = (value) => {
    setIsHeaderSaving(true);
    if (params.store?.columnIdsByName && params.store?.columnIdsByName?.[value]){
      params.store.view.options.rowViewHeader = params.store?.columnIdsByName[value];
    } else {
      params.store.view.options.rowViewHeader = value;
    }
    params.store.saveViewOptions();
    let headerName = getFormattedHeaderName();
    setHeaderTitle(headerName || `Row ${data?.row_id}`);
    setSettingsOpen(false);
    setIsHeaderSaving(false);
  }

  const getFormattedTextValue = (value, columnTypeId, store) => {
    if (value && typeof value !== "string") {
      value = value.toString();
    }
    var val = value && value !== "undefined" ? value : "";

    if(columnTypeId == "24" && val != "" && val != "+r0w" && val != "+" && store?.validateUrl(val)) {
      var urlColumnHref;
      if (!val.match(/^[a-zA-Z]+:\/\//)) {
        urlColumnHref = "http://" + val;
      } else {
        urlColumnHref = val;
      }
      return '<a href="' + urlColumnHref + '" target="_blank">' + val + "</a>";
    }

    if (
      val.match(
        /^(([^<>()\[\]\\.,;\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
    ) {
      return val.replace(
        /^(([^<>()\[\]\\.,;\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        function (url) {
          var urlHref;
          if (
            url.match(
              /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
          ) {
            urlHref = "mailto:" + url;
          }
          return '<a href="' + urlHref + '" target="_blank">' + url + "</a>";
        }
      );
    } else {
      return val.replace(
        /((?:(http|https|Http|Https|rtsp|Rtsp):\/\/(?:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=\]\[]|(?:\%[a-fA-F0-9]{2})){1,64}(?:\:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=\]\[]|(?:\%[a-fA-F0-9]{2})){1,25})?\@)?)?((?:(?:[a-zA-Z0-9][a-zA-Z0-9\-]{0,64}\.|([a-zA-Z]*[0-9]*\@))+(?:(?:aero|arpa|asia|a[cdefgilmnoqrstuwxz])|(?:biz|b[abdefghijmnorstvwyz])|(?:cat|com|coop|c[acdfghiklmnoruvxyz])|d[ejkmoz]|(?:edu|e[cegrstu])|f[ijkmor]|(?:gov|g[abdefghilmnpqrstuwy])|h[kmnrtu]|(?:info|int|i[delmnoqrst])|(?:jobs|j[emop])|k[eghimnrwyz]|l[abcikrstuvy]|(?:mil|mobi|museum|m[acdghklmnopqrstuvwxyz])|(?:name|net|n[acefgilopruz])|(?:org|om)|(?:pro|p[aefghklmnrstwy])|qa|r[eouw]|s[abcdeghijklmnortuvyz]|(?:tel|travel|t[cdfghjklmnoprtvwz])|u[agkmsyz]|v[aceginu]|w[fs]|y[etu]|z[amw]))|(?:(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9])\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[0-9])))(?:\:\d{1,5})?)(\/(?:(?:[a-zA-Z0-9\;\/\?\:\@\&\=\#\~\-\.\+\!\*\'\(\)\,\]\[\_])|(?:\%[a-fA-F0-9]{2}))*)?(?:\b|$)/gi,
        function (url) {
          var urlHref;
          if (
            url.match(
              /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
          ) {
            urlHref = "mailto:" + url;
          } else if (!url.match(/^[a-zA-Z]+:\/\//)) {
            urlHref = "http://" + url;
          } else {
            urlHref = url;
          }
          return '<a href="' + urlHref + '" target="_blank">' + url + "</a>";
        }
      );
    }
  };

  const getCheckBox = (value) => {
    if (value == '1') {
      return `<svg
        xmlns="http://www.w3.org/2000/svg"
        width="17.6"
        height="17.6"
        viewBox="0 0 16 16"
        class="flex-none"
        style="shapeRendering: geometricprecision"
      >
        <path
          fillRule="evenodd"
          fill=${COLORS.THEME_COLOR}
          d="M5.944 12.305a1.031 1.031 0 0 0 1.433-.009l5.272-5.181A1.483 1.483 0 0 0 12.661 5a1.468 1.468 0 0 0-2.109.025L7.008 8.701a.465.465 0 0 1-.685-.01l-.587-.641A1.42 1.42 0 0 0 3.661 8a1.473 1.473 0 0 0 .017 2.106l2.266 2.199z"
        ></path>
      </svg>`;
    } else {
      return `<svg 
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24" 
                width="17.6"
                height="17.6"
                >
                <path 
                  fillRule="evenodd"
                  fill=${COLORS.THEME_COLOR}
                  d="M19 5v14H5V5h14m0-1H5c-1.1 0-1 .9-1 1v14c0 1.1.9 1 1 1h14c1.1 0 1-.9 1-1V5c0-1.1-.9-1-1-1z"
                </path>
              </svg>`;
    }
    return `<div/>`
  };

  const formatDateAndTime = (value, colOptions, columnTypeId) => {
    return `${formatDate(value, "YYYY-MM-DD", colOptions ? colOptions["format"] : (columnTypeId == "4" ? "M j, Y" : "M j, Y g:iA"))} ${formatTime(value, columnTypeId)}`;
  };

  const FilePathFormat = (file) => {
    let fileFullPath = file.path;
    if (fileFullPath) {
      fileFullPath = fileFullPath.includes("http://tigersheet.s3.amazonaws.com") ? fileFullPath.replace("http", "https") : fileFullPath;
    } else if (file.filepath) {
      let serverPath = "https://tigersheet.s3.amazonaws.com/";
      fileFullPath = serverPath.concat(file.filepath);
    } else if (file.fullpath) {
      fileFullPath = file.fullpath.includes("https") ? file.fullpath : file.fullpath.includes("http") ? file.fullpath.replace("http", "https") : file.fullpath;
    }
    return fileFullPath;
  };

  const formatFiles = (value, isSubsheet = false) => {
    let filesData = [];
    if (Array.isArray(value)) {
        filesData = value ?? [];
    } else if (value && typeof value === 'object') {
        filesData = [value] ?? [];
    }

    if (value && typeof value === 'string') {
      try {
          const parsedFiles = JSON.parse(value);
          filesData = Array.isArray(parsedFiles) ? parsedFiles ?? {} : [parsedFiles];
      } catch (e) {
          filesData = [];
      }
    }

    let formattedFiles = '';
    if (filesData.length > 0) {
      formattedFiles = filesData
        .map((file, index) => {
          const fileName = file.name || "File";
          const  fileUrl = FilePathFormat(file);
          const linespace = index < filesData.length - 1 ? (isSubsheet ? "10px" : "5px") : "0px";
          return `<div style="margin-bottom: ${linespace};"><a href="${fileUrl}" target="_blank">${fileName}</a></div>`;
        })
        .join("");
    }
    
    return formattedFiles;
  };

  const formatSubsheetData = async (subsheetRowData, listColumns) => {
    try {
      let formattedSubsheetData = [];
      for (const row of subsheetRowData) {
        let [data, creationRelatedData] = await getRowDataToView(row, listColumns, true);
        data['Id'] = row?.row_id ?? '';
        formattedSubsheetData.push({ ...data, ...creationRelatedData });
      }
      return formattedSubsheetData;
    } catch (error) {
      console.log('Error: ', error);
      return '';
    }
  };

  const fetchAndFormatAllSubsheetsData = async (payload, allSubsheetColsInfo) => {
    try {
      const sub_list_ids = JSON.parse(payload['sub_list_ids']);
      payload['user_id'] = window.LOGGED_IN_USER.user_id ?? '';
      let allSubsheetsData = {};
      const resp = payload['parent_row_id'] ? await ApiClient.List.getSubsheetData(payload) : {};

      if (resp && resp.success && resp.data) {
        allSubsheetsData = resp.data ?? {};
      } else {
        const response = payload['parent_row_id']
          ?
          await ApiClient.List.getRowData({
            list_id: payload['parent_list_id'] ?? window.appStore.containerStore?.listId,
            row_id: payload['parent_row_id']
          })
          :
          {};
        if (response && response.data) {
          for (const subsheet of sub_list_ids) {
            const columnId = 'column_' + allSubsheetColsInfo[subsheet]['columnId'];
            allSubsheetsData[subsheet] = response.data?.[columnId] ?? [];
          }
        }
      }

      let formattedSubsheetsData = {};
      for (const subsheet of sub_list_ids) {
        const columnName = allSubsheetColsInfo[subsheet]['columnName'];
        const subsheetRowData = await formatSubsheetData(allSubsheetsData[subsheet], allSubsheetColsInfo[subsheet]?.subsheetColumns);
        formattedSubsheetsData[columnName] = subsheetRowData ?? '';
      }

      return formattedSubsheetsData;

    } catch (error) {
      console.log('Failed fetching subsheet rows: ', error);
    }
  };

  const getFormattedCommentsData = (comments) => {
    let commentsArray = [];
    let result = '';
    for (let i = comments.length - 1; i >= 0; i--) {
      let commentObject = comments[i];
      const { description, created_at, created_by_name } = commentObject;
      const istDate = new Date(created_at);
      const finalDate = params.store.convertIstToCurrentTimeZone(istDate);
      const formattedDate = `<span style="font-size: 9px;">${moment(finalDate).fromNow()}</span>`;
      const indexStyling = `<div style=" display:inline-block;vertical-align: text-top; background-color: #3f51b5; color: white; border-radius: 50%; width: ${i + 1 >= 100 ? '20px' : i + 1 >= 10 ? '17px' : '15px'}; height: ${i + 1 >= 100 ? '20px' : i + 1 >= 10 ? '17px' : '15px'}; text-align: center; line-height: ${i + 1 >= 100 ? '20px' : i + 1 >= 10 ? '17px' : '15px'}; margin-right: 5px; font-size: 9px">${i + 1}</div>`;
      const createdByAndDate = `<span style="padding: 9px; border-radius: 20px; text-align: center; display: inline-block; align-items: center; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; background-color: #e7e7e7; color: #4e4e4e; line-height: 0px; ">${created_by_name} ${formattedDate}</span>`;
      const formattedComment = `<div style="display: inline-block; vertical-align: top;">${indexStyling}</div><div style="display: inline-block; max-width: calc(100% - 50px); vertical-align: top; margin-bottom: 5px; color: #3f51b5; max-height: 95px; overflow-y: auto">${description}</div><span style="display: flex; align-items: center; max-width: 95%;">${createdByAndDate}</span>`;
      commentsArray.push(formattedComment);
    }

    if (commentsArray.length > 0) {
      result = commentsArray.join('<br>');
    }

    return result;
  };

  function formatNumberColumn(val, opts) {
    if (opts) {
      let decimals = typeof opts.decimals != "undefined" ? opts.decimals : 2;
      if (typeof opts.number_format != "undefined") {
        return Util.formatNumber(
          Number(val).toFixed(decimals),
          opts.number_format,
          decimals
        );
      }
      return Number(val).toFixed(decimals);
    }
    return Number(val).toFixed(2);
  }

  function formatPercentageColumn(val, opts) {
    if(isNaN(val)){
      return "0 %";
    }
    if (opts) {
      let decimals = typeof opts.decimals != "undefined" ? opts.decimals : 0;
      if (typeof opts.number_format != "undefined") {
        var num = Util.formatNumber(
          Number(val*100).toFixed(opts.decimals-2),
          opts.number_format,
          decimals
        );
        return num+" %";
      }
      return Number(val*100).toFixed(decimals-2)+" %";
    }
    return Number(val*100).toFixed(2)+" %";
  }

  function getFormattedNumberValue(columnTypeId, value, columnOptions) {
    try {
      if (
        columnTypeId == "17" &&
        value &&
        value !== ""
      ) {
        return `${columnOptions["currency_symbol"] ? (columnOptions?.symbol_at_end ? '' : columnOptions["currency_symbol"]) : "$"} ${formatNumberColumn(
          value,
          columnOptions
        )} ${columnOptions?.symbol_at_end ? columnOptions.currency_symbol : ''}`;
      }
      else if(columnTypeId == "22"){
        return formatPercentageColumn(value, columnOptions)
      }
      return formatNumberColumn(value, columnOptions);
    } catch (error) {
      console.error("Error in getFormattedNumberValue:", error);
      return value;
    }
  }

  const convertStringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).substr(-2);
    }
    return color;
  }

  const getBackGroundColor = (value, params) => {
    if (params?.columnRaw?.name != "Operation") {
      return "grey";
    }
    return convertStringToColor(value);
  }

  const getFormattedDropdownValue = (value, colOptions, columnTypeId) => {
    try {
      const options = (colOptions?.options || []).map((option, index) => ({
        label: typeof option === "string" ? option.trim() : option,
        background: colOptions.colors?.[index] || "grey",
      }));

      let valuesArrToShow = [];

      if (columnTypeId !== "3") {
        const values = value ? value.split(",") : [];
        values.forEach((val) => {
          const found = options.find((option) => option.label === val.trim());
          valuesArrToShow.push(found || { label: val.trim(), background: "grey" });
        });
      } else {
        if (value.trim() !== "") {
          const trimmedValue = typeof value === "string" ? value.trim() : value;
          const match = options.find((option) => option.label === trimmedValue);
          valuesArrToShow.push(match ? match : { label: trimmedValue, background: "grey" });
        }
      }

      return valuesArrToShow.map((option, idx) => {
        if (option) {
          const trimmedValue = option.label.length > 15 ? option.label.slice(0, 15) + "..." : option.label;

          const color = tinycolor(option.background).getLuminance() > 0.5
            ? tinycolor(option.background).darken(60).toString()
            : COLORS.PRIMARY_COLOR;
          
          const backgroundColor = option
            ? option.background
            : trimmedValue
            ? (params.store?.listConfig?.list?.is_log_sheet === 1 ? getBackGroundColor(option.label) : "grey")
            : "transparent";

          return (
            <Tooltip 
              key={idx} 
              title={option.label.length > 15 ? option.label : ""} 
              // arrow
              placement="bottom"
              PopperProps={{
                className: 'rowDetailTooltip',
              }}
            >
              <div
                className="dropDownChips"
                style={{
                  color,
                  backgroundColor,
                  margin: '3px',
                  padding: '12px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  display: 'inline-block',
                  alignItems: 'center',
                  height: '26px',
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'default',
                  lineHeight: '0px'
                }}
              >
                {trimmedValue}
              </div>
            </Tooltip>
          );
        }
      });  
    } catch (err) {
      console.log('formatting dropdown value error: ', err);
      return "";
    }
  };

  const renderValue = (value) => {
    if (value === null || value === undefined || value === "") return "";


    if (typeof value === 'object' && value.$$typeof === Symbol.for('react.element')) {
      return value;
    }

    if (typeof value === "string" && /<\/?[a-z][\s\S]*>/i.test(value)) {
      return (
        <div
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      );
    }

    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
      const orderedKeys = (obj) => {
        const keys = Object.keys(obj);
        if (keys.includes("Id")) {
          return ["Id", ...keys.filter((k) => k !== "Id" && k !== "Created By"), "Created By"];
        }
        return keys;
      };

      return (
        <TableContainer 
          component={Paper} 
          style={isExpanded ? expandedSubsheetStyles.container : subsheetStyles.container}
        >
          <Table size="small" stickyHeader>
            <TableHead style={{ backgroundColor: "#334155" }}>
              <TableRow>
                {orderedKeys(value[0]).map((key) => (
                  <TableCell key={key} style={subsheetStyles.headerCell} >
                    {key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {value.map((row, idx) => (
                <TableRow key={idx}>
                  {orderedKeys(row).map((key) => (
                    <TableCell key={key} style={subsheetStyles.tableCell}>
                      {
                        React.isValidElement(row[key]) ? (
                          row[key]
                        ) : typeof row[key] === "string" && /<\/?[a-z][\s\S]*>/i.test(row[key]) ? (
                          <div
                            dangerouslySetInnerHTML={{ __html: row[key] }}
                            style={isExpanded ? expandedSubsheetStyles.rowCell : subsheetStyles.rowCell}
                          />
                        ) : (
                          <ExpandableText text={String(row[key])} isSubsheet={true} />
                        )
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (typeof value === "string") {
      return <ExpandableText text={value} isSubsheet={false} />;
    }

    return value;
  };

  const ExpandableText = ({ text, isSubsheet = false }) => {
    const [expanded, setExpanded] = useState(false);
    const toggle = () => setExpanded(!expanded);
    const trimSize = isExpanded ? 250 : 150;
    const style = (isExpanded && isSubsheet) ? expandedSubsheetStyles.rowCell : (!isExpanded && isSubsheet) ? subsheetStyles.rowCell : {};

    if (!text) return '';

    if (text.length <= trimSize) {
      return <div style={style}>{text}</div>;
    }

    return (
      <div style={style}>
        {expanded ? text : `${text.slice(0, trimSize)}... `}
        <button
          onClick={toggle}
          style={{ color: "#4463ff", marginLeft: "4px", cursor: "pointer", textDecoration: "underline", border: "none", backgroundColor: "#ffffff" }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      </div>
    );
  };

  const getStyles = (valType, isSubsheet) => {
    let style = styles;
    if (valType === 'value' && isSubsheet) {
      return isExpanded ? expandedViewStyles.subsheetValue : styles.subsheetValue;
    } else if (valType === 'value' && !isSubsheet) {
      return isExpanded ? expandedViewStyles.value : styles.value;
    } else {
      style = (isSubsheet && isExpanded) ? expandedSubsheetStyles : 
              (isSubsheet && !isExpanded) ? subsheetStyles :
              (!isSubsheet && isExpanded) ? expandedViewStyles : 
              styles;
    }
    return style[valType];
  };

  const getRowDataToView = async (data, columns, isSubsheet = false) => {
    try {
      let formattedRowData = {};
      let rowCreationAndUpdationData = {};
      let allSubsheetsPayload = {};
      let allSubsheetColsInfo = {};
      if (!isSubsheet && params.store.checkIfHiddenFieldsExists()) {
        if (params.store && params.store?.hiddenColumnsData && Object.keys(params.store.hiddenColumnsData).length > 0) {
          data = {...data, ...params.store.hiddenColumnsData[data['row_id']]};
        }
      }
      for (const column of columns) {
        if (column.id == 'created_at') {
          rowCreationAndUpdationData['Created On'] = data[column.id];
        } else if (column.id == 'updated_at') {
          rowCreationAndUpdationData['Updated On'] = data[column.id];
        } else if (column.alias_name == 'createdBy') {
          if (data.hasOwnProperty('column_' + column.id)) {
            rowCreationAndUpdationData['Created By'] = (data['column_' + column.id] === 'null' || data['column_' + column.id] === null) ? '' : getFormattedTextValue(data['column_' + column.id], '14', params.store);
          }
        } else {
          const value = data['column_' + column.id] ?? '';
          const columnTypeId = column['column_type_id'];
          const colOptions = normalizeColOptions(column?.options);
          let formattedVal = '';
          switch (columnTypeId) {
            case '1':
            case '8':
            case '14':
            case '20':
            case '24':
              formattedVal = getFormattedTextValue(value, columnTypeId, params.store);
              break;
          
            case '5':
            case '17':
            case '22':
              formattedVal = getFormattedNumberValue(columnTypeId, value, colOptions);
              break;
            
            // case '17':
            //   const currSymbol = colOptions?.currency_symbol ?? '';
            //   formattedVal = currSymbol + value;
            //   break;
            
            // case '22':
            //   formattedVal = value + '%';
            //   break;

            case '12':
              formattedVal = getCheckBox(value);
              break;

            case '7':
            case '19':
              if (colOptions?.formula_column?.column_type_id && (colOptions?.formula_column?.column_type_id == '17' || colOptions?.formula_column?.column_type_id == '5')) {
                const formulaColOptions = normalizeColOptions(colOptions?.formula_column?.options);
                formattedVal = getFormattedNumberValue(colOptions?.formula_column?.column_type_id, value, formulaColOptions);
                // const formulaCurrSymbol = formulaColOptions?.currency_symbol ?? '';
                // formattedVal = formulaCurrSymbol + value;
              } else if (colOptions?.formula_column?.column_type_id && (colOptions?.formula_column?.column_type_id == '4' || colOptions?.formula_column?.column_type_id == '16')) {
                const formulaColOptions = normalizeColOptions(colOptions?.formula_column?.options);
                formattedVal = formatDateAndTime(value, formulaColOptions, colOptions?.formula_column?.column_type_id) ?? '';
              } else if (colOptions?.formula_column?.column_type_id && colOptions?.formula_column?.column_type_id == '1'){
                formattedVal = getFormattedTextValue(value, colOptions?.formula_column?.column_type_id, params.store);
              } else {
                formattedVal = value;
              }
              break;

            case '4':
            case '16':
              formattedVal = formatDateAndTime(value, colOptions, columnTypeId) ?? '';
              break;

            case '9':
              formattedVal = formatFiles(value, isSubsheet) ?? '';
              break;
            
            case '3':
            case '11':
            case '13':
              formattedVal = <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '150px' }}>{getFormattedDropdownValue(value, colOptions, columnTypeId)}</div> ?? '';
              break;

            case '6':
              if (column.list_columns.length > 2 && Number(value) > 0) {
                const subsheetListId = colOptions?.sub_list_id;
                if (window.appStore.containerStores?.[subsheetListId] && window.appStore.containerStores?.[subsheetListId]?.currentSubsheetStore && Object.keys(window.appStore.containerStores?.[subsheetListId]?.currentSubsheetStore).length > 0 && window.appStore.containerStores?.[subsheetListId]?.currentSubsheetStore?.parentRowId == data?.row_id) {
                  let newRows = window.appStore.containerStores[subsheetListId].currentSubsheetStore.newRows.filter(row => row.row_id > 0);
                  const subsheetRowData = [...window.appStore.containerStores?.[subsheetListId]?.currentSubsheetStore?.allRowsData, ...newRows];
                  formattedVal = formatSubsheetData(subsheetRowData, column?.list_columns) ?? '';
                } else {
                  allSubsheetsPayload['parent_row_id'] = data?.row_id;
                  allSubsheetsPayload['parent_list_id'] = column?.list_id;
                  if (!allSubsheetsPayload['sub_list_ids']) {
                    allSubsheetsPayload['sub_list_ids'] = [];
                  } 
                  allSubsheetsPayload['sub_list_ids'].push(subsheetListId);
                  allSubsheetColsInfo[subsheetListId] = {
                    columnId: column?.id,
                    columnName: column.name,
                    subsheetColumns: column?.list_columns
                  };
                }
              } else {
                formattedVal = '';
              }
              break;
            
            default:
              formattedVal = value;
          }
          if (column?.alias_name !== 'star') {
            formattedRowData[column.name] = (formattedVal === 'null' || formattedVal === null) ? '' : formattedVal;
          }
        }
      }

      if (Object.keys(allSubsheetColsInfo).length > 0) {
        allSubsheetsPayload['sub_list_ids'] = JSON.stringify(allSubsheetsPayload['sub_list_ids']);
        const formattedSubsheetsData = await fetchAndFormatAllSubsheetsData(allSubsheetsPayload, allSubsheetColsInfo);
        formattedRowData = {...formattedRowData, ...formattedSubsheetsData};
      }

      if (data?.comments && data?.comments.length > 0) {
        const formattedCommentsData = getFormattedCommentsData(data.comments);
        if (formattedCommentsData !== '') {
          rowCreationAndUpdationData = {...rowCreationAndUpdationData, [`Notes/Comments (${data.comments.length})`]: formattedCommentsData};
        }
      }

      return [formattedRowData, rowCreationAndUpdationData];
    } catch (error) {
      console.error("Error in getRowDataToView:", error);
      return [{}, {}];
    }
  }

  return (
    <>
      <style>{keyframes}</style>

      <div style={isExpanded ? expandedViewStyles.overlay : styles.overlay} onClick={onClose}>
        <div style={isExpanded ? expandedViewStyles.backdrop : styles.backdrop}></div>
        <div style={isExpanded ? expandedViewStyles.panel : styles.panel} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isHeaderSaving ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
                ) : <h2 style={styles.title}>{headerTitle}</h2>
              }

              {isAdmin && (
                <>
                  <IconButton ref={settingsButtonRef} onClick={toggleSettings} size="small" disabled={isHeaderSaving}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                  {settingsOpen && settingsButtonRef.current && !isHeaderSaving && (
                    <Popover
                      open={settingsOpen}
                      anchorEl={settingsButtonRef.current}
                      onClose={() => setSettingsOpen(false)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      style={{ zIndex: 10001 }}
                      PaperProps={{
                        style: {
                          padding: 16,
                          width: 320,
                          maxHeight: 400,
                          overflowY: "auto",
                          borderRadius: 8,
                          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          Pick a Column for Header Name
                        </Typography>
                        <IconButton size="small" onClick={() => setSettingsOpen(false)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Autocomplete
                        freeSolo
                        disableClearable
                        options={options}
                        openOnFocus
                        ListboxProps={{
                          style: {
                            maxHeight: 300,
                            overflowY: "auto",
                            maxWidth: 300,
                            overflowX: "hidden",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          },
                        }}
                        noOptionsText={
                          'Press Enter to set your input as Header Name'
                        }
                        onChange={(e, newValue) => saveAndReplaceHeaderTitle(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Header name"
                            variant="outlined"
                            size="small"
                            autoFocus
                            onFocus={(e) => {
                              e.target.select();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                saveAndReplaceHeaderTitle(e.target.value);
                              }
                            }}
                            helperText={
                            params.inputProps.value !== '' && !options.includes(params.inputProps.value)
                              ? 'Press Enter to set your input as Header Name'
                              : ''
                          }
                          />
                        )}
                        PopperComponent={(props) => (
                          <div {...props} style={{
                            zIndex: 2147483648,
                            backgroundColor: "#fff",
                            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
                            borderRadius: 4,
                            padding: "4px 0",
                            maxHeight: 300,
                            // maxWidth: 300,
                            overflowY: "auto",
                          }} />
                        )}
                      />
                    </Popover>
                  )}
                </>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <IconButton onClick={toggleExpanded}>
                {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <button
                style={styles.closeBtn}
                onMouseOver={(e) => (e.target.style.color = "#000")}
                onMouseOut={(e) => (e.target.style.color = "#666")}
                onClick={onClose}
              >
                ✕
              </button>
            </div>
          </div>

          <div style={{ ...styles.body, overflow: "auto" }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
              ) : 
              Object.entries({ ...formattedData, ...metaData }).map(([key, value]) => (
                <div
                  key={key} 
                  style={styles.row}
                >
                  <div style={isExpanded ? expandedViewStyles.key : styles.key}>{key}</div>
                  <div style={getStyles('value', Array.isArray(value))}>{renderValue(value)}</div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default RowDetailPanel;
